import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { connect, useSelector, useDispatch } from 'react-redux';
import { Icon, Input, Button, Tooltip, Skeleton, Tabs } from 'antd';

import Layout from '~/components/UI/Layout/Layout';
import { ModuleHeader, ModuleHeaderWrapper } from '~/components/ModuleHeader';
import SettingsBasicForm from './SettingsBasicForm';
import SettingsCompanyForm from './SettingsCompanyForm';
import ChangePasswordForm from './ChangePasswordForm';
import ModuleMonthlyForm from './ModuleMonthlyForm';
import { SporadicDocumentTypes } from './SporadicDocumentTypes';
import { DocumentTypesMatrix } from './DocumentTypesMatrix';
import { ChecklistTasks } from './ChecklistTasks';
import { SocietarioStages } from './SocietarioStages';
import { ExtratoBancarioSettings } from './ExtratoBancario';
import RegisterTasksList from '~/components/RegisterTasks/List';
import RegisterTasksForm from '~/components/RegisterTasks/Form';
import RegisterTasksCustomers from '~/components/RegisterTasks/Customers';
import DrawerBulk from '~/_domains/tasks/drawerBulk';
import { loadCustomers } from '~/_domains/customers/drawer/actions';
import { loadTasks } from '~/components/RegisterTasks/actions';
import { WhatsappByDepartment } from './WhatsappByDepartment';
import SystemVariables from './SystemVariables';
import Wpp from './Wpp';
import InstagramIntegration from './InstagramIntegration';
import VersionLog from './VersionLog';
import UsersContent from './UsersContent';
import PermissionsContent from './PermissionsContent';
import { PERMISSIONS } from '~/constants/permissions.constants';
import SettingsCardGrid from './SettingsCardGrid';
import SettingsDrawer from './SettingsDrawer';
import { findSettingsItem } from './settingsItems';

// ==========================================
// REGISTER TASKS CONTENT (small wrapper)
// ==========================================

var RegisterTasksContent = function(props) {
    var dispatch = useDispatch();
    var location = useLocation();
    var history = useHistory();
    var isLoadingState = useState(true);
    var isLoading = isLoadingState[0];
    var setIsLoading = isLoadingState[1];
    var taskState = useState(null);
    var task = taskState[0];
    var setTask = taskState[1];
    var modalState = useState({ isVisible: false, data: null });
    var modal = modalState[0];
    var setModal = modalState[1];

    var activeTabState = useState('tasks');
    var activeTab = activeTabState[0];
    var setActiveTab = activeTabState[1];

    useEffect(function() {
        Promise.all([
            dispatch(loadTasks({ showLoading: false })),
            dispatch(loadCustomers({ showLoading: false })),
        ]).finally(function() {
            setIsLoading(false);
        });
    }, [dispatch]);

    // Open create modal when + button clicked from top bar (only on tasks tab)
    var prevTriggerRef = React.useRef(props.addTrigger);
    useEffect(function() {
        if (props.addTrigger > 0 && props.addTrigger !== prevTriggerRef.current) {
            if (activeTab === 'tasks') {
                setModal({ isVisible: true, data: null });
            }
        }
        prevTriggerRef.current = props.addTrigger;
    }, [props.addTrigger, activeTab]);

    var showModal = function(data) {
        setModal({ isVisible: true, data: data || null });
    };

    var hideModal = function() {
        setModal({ isVisible: false, data: null });
    };

    if (isLoading) {
        return React.createElement(Skeleton, { active: true, paragraph: { rows: 6 } });
    }

    return (
        <div>
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                animated={false}
                style={{ marginBottom: 8 }}
                tabBarExtraContent={
                    <Button
                        size="small"
                        icon="table"
                        onClick={function() { history.push('/configuracoes/cadastro-de-tarefas/start'); }}
                    >
                        Matriz Clientes x Tarefas
                    </Button>
                }
            >
                <Tabs.TabPane tab="Tarefas" key="tasks">
                    <RegisterTasksList
                        embedded
                        externalSearch={props.searchTerm}
                        isModalVisible={modal.isVisible}
                        openCustomers={function(t) { setTask(t); }}
                        isCustomersModalVisible={task !== null}
                        showModal={showModal}
                    />
                </Tabs.TabPane>
                <Tabs.TabPane tab="Tipos de Documentos" key="document-types">
                    <DocumentTypesMatrix
                        searchTerm={props.searchTerm}
                        onOpenTask={function(t) { showModal(t); }}
                    />
                </Tabs.TabPane>
            </Tabs>
            <RegisterTasksForm visible={modal.isVisible} onClose={hideModal} data={modal.data} />
            <RegisterTasksCustomers
                visible={task !== null}
                task={task}
                onClose={function() { setTask(null); }}
            />
            <DrawerBulk autoOpen={location.pathname === '/configuracoes/cadastro-de-tarefas/start'} />
        </div>
    );
};

// ==========================================
// MAIN SETTINGS COMPONENT
// ==========================================

var CHECKLIST_TYPE_MAP = { 'entrada': 2, 'entrada-troca': 4, 'saida-encerramento': 1, 'saida-troca': 3 };

var ScreensAccount = function(props) {
    var user = props.user;
    var location = useLocation();
    var isAdmin = !!user.id && user.permissions_group_id === 1;
    var userPermissions = (user && user.permissions) || [];
    var hasPermission = function(perm) {
        if (isAdmin) return true;
        return userPermissions.indexOf(perm) !== -1;
    };

    var renderContent = function(pathname, extra) {
        var searchTerm = (extra && extra.searchTerm) || '';
        var addTrigger = (extra && extra.addTrigger) || 0;

        if (pathname === '/configuracoes/perfil') {
            return React.createElement(SettingsBasicForm);
        }
        if (pathname === '/configuracoes/senha') {
            return React.createElement(ChangePasswordForm);
        }
        if (pathname.indexOf('/configuracoes/empresa') === 0 && hasPermission(PERMISSIONS.settings.empresa.view)) {
            var empresaParts = pathname.split('/');
            var empresaSection = empresaParts[3] || 'dados';
            return React.createElement(SettingsCompanyForm, { section: empresaSection });
        }
        if (pathname.indexOf('/configuracoes/tarefas-do-checklist') === 0 && hasPermission(PERMISSIONS.settings.checklistTasks.view)) {
            var parts = pathname.split('/');
            var type = parts[3] || 'entrada';
            var status = CHECKLIST_TYPE_MAP[type] || 2;
            return React.createElement(ChecklistTasks, { initialStatus: status, addTrigger: addTrigger, searchTerm: searchTerm });
        }
        if (pathname === '/configuracoes/extrato-bancario' && hasPermission(PERMISSIONS.extratoBancario.view)) {
            return React.createElement(ExtratoBancarioSettings);
        }
        if (pathname.indexOf('/configuracoes/etapas-do-societario') === 0 && hasPermission(PERMISSIONS.settings.societarioStages.view)) {
            var stageParts = pathname.split('/');
            return React.createElement(SocietarioStages, { type: stageParts[3] || 'alteracao' });
        }
        // O sufixo /start abre a Matriz Clientes x Tarefas por cima da aba
        if ((pathname === '/configuracoes/cadastro-de-tarefas' || pathname === '/configuracoes/cadastro-de-tarefas/start') && hasPermission(PERMISSIONS.settings.taskRegistration.view)) {
            return React.createElement(RegisterTasksContent, { searchTerm: searchTerm, addTrigger: addTrigger });
        }
        if (pathname === '/configuracoes/mensalidades' && hasPermission(PERMISSIONS.settings.monthlyModule.view)) {
            return React.createElement(ModuleMonthlyForm);
        }
        if (pathname.indexOf('/configuracoes/variaveis/docs/') === 0 && hasPermission(PERMISSIONS.settings.sporadicDocs.view)) {
            var deptParts = pathname.split('/');
            var department = deptParts[4] || 'legalizacao';
            return React.createElement(SporadicDocumentTypes, { department: department, searchTerm: searchTerm, addTrigger: addTrigger });
        }
        if (pathname.indexOf('/configuracoes/variaveis/') === 0 && hasPermission(PERMISSIONS.settings.systemVariables.view)) {
            var varParts = pathname.split('/');
            var section = varParts[3] || 'origens';
            return React.createElement(SystemVariables, { section: section, searchTerm: searchTerm, addTrigger: addTrigger });
        }
        if (pathname === '/configuracoes/whatsapp-por-departamento' && hasPermission(PERMISSIONS.settings.integracoes.view)) {
            return React.createElement(WhatsappByDepartment);
        }
        if (pathname === '/configuracoes/wpp') {
            return React.createElement(Wpp, { user: user });
        }
        if (pathname === '/configuracoes/integracoes/instagram') {
            return React.createElement(InstagramIntegration);
        }
        if (pathname === '/configuracoes/usuarios' && hasPermission(PERMISSIONS.users.view)) {
            return React.createElement(UsersContent, { user: user });
        }
        if (pathname === '/configuracoes/permissoes' && hasPermission(PERMISSIONS.settings.permissoes.view)) {
            return React.createElement(PermissionsContent);
        }
        if (pathname === '/configuracoes/versoes' && isAdmin) {
            return React.createElement(VersionLog);
        }
        return null;
    };

    return (
        <Layout>
            <ModuleHeaderWrapper>
                <ModuleHeader
                    breadcrumbs={["Configurações"]}
                    title="Configurações"
                />
            </ModuleHeaderWrapper>
            <SettingsCardGrid hasPermission={hasPermission} isAdmin={isAdmin} company={user.tenant} userEmail={user.email} />
            <SettingsDrawer renderContent={renderContent} user={user} />
        </Layout>
    );
};

var mapStateToProps = function(state) {
    return {
        user: state.user
    };
};

export default connect(mapStateToProps)(ScreensAccount);
